// F-087 — Assemblées générales : convocation, résolutions, intentions de vote, procès-verbal, délai de contestation.
import { useState } from "react";
import { del, post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateLongFmt } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Checkbox, Empty, ErrorBox, FileDrop, Input, Loading, Modal, MoneyInput, Notice, PageHeader, Select, StatusChip, Table, Textarea, fieldError } from "@core/ui";
import { Amount, BuildingSelect, DocLink, required, useAction, useRefs } from "./shared";

type Resolution = {
  id: string;
  number: number;
  title: string;
  description: string;
  majority: string;
  estimated_cost_cents: number | null;
  intention: string;
  outcome: string;
  source_ref: string;
  affects_charges: boolean;
  task_id: string | null;
};
type Meeting = {
  id: string;
  building_id: string;
  building_label: string;
  title: string;
  kind: string;
  meeting_on: string;
  location: string;
  convocation_document_id: string | null;
  convocation_filename: string | null;
  minutes_document_id: string | null;
  minutes_filename: string | null;
  minutes_notified_on: string | null;
  status: string;
  notes: string;
  contest_deadline: string | null;
  contest_open: boolean;
  resolutions?: Resolution[];
};

const STATUS: Record<string, string> = { convoquee: "Convoquée", tenue: "Tenue, procès-verbal attendu", pv_recu: "Procès-verbal reçu" };
const MAJORITY: Record<string, string> = { art24: "Article 24", art25: "Article 25", art26: "Article 26", unanimite: "Unanimité", autre: "Autre" };
const INTENTION: Record<string, string> = { a_decider: "À décider", pour: "Pour", contre: "Contre", abstention: "Abstention" };
const OUTCOME: Record<string, string> = { inconnue: "Résultat non renseigné", adoptee: "Adoptée", rejetee: "Rejetée", non_votee: "Non votée" };

export function MeetingsPage() {
  const org = useOrg();
  const refs = useRefs();
  const [building, setBuilding] = useState("");
  const q = useGet<{ items: Meeting[]; notice: string }>(org.api(`/condo/meetings${building ? `?building_id=${building}` : ""}`));
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<Meeting | "new" | null>(null);
  const canEdit = org.can("operate");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Immeubles"
        title="Assemblées générales"
        intro="Préparez vos intentions de vote, conservez convocations et procès-verbaux, et gardez la trace des décisions qui touchent vos charges. Bailivo ne vote pas et n’invente aucune décision."
        actions={canEdit ? <Button onClick={() => setEditing("new")}>Nouvelle assemblée</Button> : null}
      />
      <div className="finances-filtres">
        <BuildingSelect refs={refs.data} value={building} onChange={setBuilding} empty="Tous les immeubles" />
      </div>
      {q.data ? <Notice>{q.data.notice}</Notice> : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucune assemblée enregistrée." /> : null}
      <div className="stack">
        {(q.data?.items ?? []).map((m) => (
          <article key={m.id} className="finances-element">
            <div className="finances-element-tete">
              <div>
                <h3>{m.title}</h3>
                <p className="small muted">
                  {m.building_label} · {dateLongFmt(m.meeting_on)} · {m.kind === "extraordinaire" ? "extraordinaire" : "ordinaire"}
                </p>
              </div>
              <div className="row">
                <Badge tone={m.status === "pv_recu" ? "succes" : "encre"}>{STATUS[m.status]}</Badge>
                {m.contest_deadline ? (
                  m.contest_open ? <StatusChip status="a_confirmer" label={`Contestation possible jusqu’au ${date(m.contest_deadline)}`} /> : <Badge>Délai de contestation écoulé</Badge>
                ) : null}
              </div>
            </div>
            <div className="finances-actions">
              <Button small onClick={() => setOpen(m.id)}>Ouvrir</Button>
            </div>
          </article>
        ))}
      </div>
      {open ? <MeetingDetail id={open} onClose={() => setOpen(null)} onEdit={(m) => setEditing(m)} /> : null}
      {editing ? <MeetingModal meeting={editing === "new" ? null : editing} defaultBuilding={building} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function MeetingModal({ meeting, defaultBuilding, onClose }: { meeting: Meeting | null; defaultBuilding: string; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const m = meeting;
  const [f, setF] = useState({
    building_id: m?.building_id ?? defaultBuilding,
    title: m?.title ?? "Assemblée générale ordinaire",
    kind: m?.kind ?? "ordinaire",
    meeting_on: m?.meeting_on ?? "",
    location: m?.location ?? "",
    minutes_notified_on: m?.minutes_notified_on ?? "",
    notes: m?.notes ?? "",
  });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title={m ? "Modifier l’assemblée" : "Nouvelle assemblée générale"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const missing = required([
                [!!f.building_id, "building_id", "Choisissez l’immeuble."],
                [!!f.meeting_on, "meeting_on", "Indiquez la date de l’assemblée."],
              ]);
              if (missing) return a.setError(missing);
              const body = { ...f, minutes_notified_on: f.minutes_notified_on || null, convocation_document_id: m?.convocation_document_id ?? null, minutes_document_id: m?.minutes_document_id ?? null };
              const r = await a.run(() => (m ? put(org.api(`/condo/meetings/${m.id}`), body) : post(org.api("/condo/meetings"), body)), { success: "Assemblée enregistrée.", refresh: [org.api("/condo/meetings")] });
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <BuildingSelect refs={refs.data} value={f.building_id} error={fieldError(a.error, "building_id")} onChange={(v) => setF({ ...f, building_id: v })} empty="Choisir…" />
        <div className="form-row">
          <Input label="Titre" required value={f.title} error={fieldError(a.error, "title")} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Select label="Type" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={[{ value: "ordinaire", label: "Ordinaire" }, { value: "extraordinaire", label: "Extraordinaire" }]} />
        </div>
        <div className="form-row">
          <Input label="Date de l’assemblée" type="date" required value={f.meeting_on} error={fieldError(a.error, "meeting_on")} onChange={(e) => setF({ ...f, meeting_on: e.target.value })} />
          <Input label="Lieu" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        </div>
        <Input
          label="Date de notification du procès-verbal"
          type="date"
          value={f.minutes_notified_on}
          help="Date de première présentation du courrier recommandé ou de réception du courriel. Elle ouvre le délai de contestation de deux mois."
          error={fieldError(a.error, "minutes_notified_on")}
          onChange={(e) => setF({ ...f, minutes_notified_on: e.target.value })}
        />
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}

function MeetingDetail({ id, onClose, onEdit }: { id: string; onClose: () => void; onEdit: (m: Meeting) => void }) {
  const org = useOrg();
  const q = useGet<Meeting>(org.api(`/condo/meetings/${id}`));
  const [resolution, setResolution] = useState<Resolution | "new" | null>(null);
  const a = useAction();
  const m = q.data;
  const canEdit = org.can("operate");
  const refresh = [org.api("/condo/meetings")];
  const uploadDoc = (kind: "convocation" | "proces_verbal") => async (files: File[]) => {
    const fd = new FormData();
    fd.append("file", files[0]);
    fd.append("kind", kind);
    await a.run(() => upload(org.api(`/condo/meetings/${id}/documents`), fd), { success: kind === "convocation" ? "Convocation jointe." : "Procès-verbal joint.", refresh });
  };
  return (
    <Modal open onClose={onClose} title={m ? `${m.title} — ${date(m.meeting_on)}` : "Assemblée générale"}>
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error ?? a.error} />
        {m ? (
          <>
            {m.contest_deadline ? (
              <Notice tone={m.contest_open ? "attention" : "info"} title={m.contest_open ? `Délai de contestation : jusqu’au ${dateLongFmt(m.contest_deadline)}` : "Délai de contestation écoulé"}>
                Procès-verbal notifié le {dateLongFmt(m.minutes_notified_on)}. Deux mois pour agir si vous étiez opposant ou défaillant. Un rappel a été ajouté à vos tâches.
              </Notice>
            ) : m.status !== "convoquee" ? (
              <Notice>Renseignez la date de notification du procès-verbal pour calculer le délai de contestation.</Notice>
            ) : null}
            <div className="finances-deux-colonnes">
              <div className="finances-colonne">
                <strong>Convocation</strong>
                {m.convocation_document_id ? <DocLink id={m.convocation_document_id} name={m.convocation_filename} /> : <span className="muted small">Non jointe</span>}
                {canEdit ? <FileDrop busy={a.busy} label={m.convocation_document_id ? "Remplacer" : "Joindre la convocation"} onFiles={uploadDoc("convocation")} /> : null}
              </div>
              <div className="finances-colonne">
                <strong>Procès-verbal</strong>
                {m.minutes_document_id ? <DocLink id={m.minutes_document_id} name={m.minutes_filename} /> : <span className="muted small">Non joint</span>}
                {canEdit ? <FileDrop busy={a.busy} label={m.minutes_document_id ? "Remplacer" : "Joindre le procès-verbal"} onFiles={uploadDoc("proces_verbal")} /> : null}
              </div>
            </div>
            <p className="small muted">Ces pièces ne sont pas partagées avec vos locataires. Partagez-les explicitement depuis « Documents » si nécessaire.</p>
            <div className="row-between">
              <h3>Résolutions</h3>
              {canEdit ? (
                <div className="finances-actions">
                  <Button small variant="secondaire" onClick={() => onEdit(m)}>Modifier l’assemblée</Button>
                  <Button small onClick={() => setResolution("new")}>Ajouter une résolution</Button>
                </div>
              ) : null}
            </div>
            {(m.resolutions ?? []).length === 0 ? <p className="muted small">Ajoutez les résolutions de l’ordre du jour pour préparer vos intentions de vote.</p> : null}
            {(m.resolutions ?? []).length ? (
              <Table caption="Résolutions">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Résolution</th>
                    <th>Votre intention</th>
                    <th>Résultat</th>
                    {canEdit ? <th><span className="sr-only">Actions</span></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(m.resolutions ?? []).map((r) => (
                    <tr key={r.id}>
                      <td>{r.number}</td>
                      <td>
                        {r.title}
                        <span className="finances-sous">
                          {MAJORITY[r.majority]}
                          {r.estimated_cost_cents ? <> · coût estimé <Amount cents={r.estimated_cost_cents} /></> : null}
                          {r.affects_charges ? " · incidence sur les charges" : ""}
                        </span>
                      </td>
                      <td>{INTENTION[r.intention]}</td>
                      <td>
                        {r.outcome === "inconnue" ? <span className="muted small">{OUTCOME[r.outcome]}</span> : <Badge tone={r.outcome === "adoptee" ? "succes" : "neutre"}>{OUTCOME[r.outcome]}</Badge>}
                        {r.source_ref ? <span className="finances-sous">Source : {r.source_ref}</span> : null}
                        {r.task_id ? <span className="finances-sous">Action de suivi ajoutée aux tâches</span> : null}
                      </td>
                      {canEdit ? (
                        <td>
                          <div className="finances-actions">
                            <Button small variant="discret" onClick={() => setResolution(r)}>Modifier</Button>
                            <Button small variant="discret" onClick={() => a.run(() => del(org.api(`/condo/meetings/${id}/resolutions/${r.id}`)), { success: "Résolution supprimée.", refresh })}>Supprimer</Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
            {canEdit ? (
              <div>
                <Button
                  small
                  variant="danger"
                  onClick={async () => {
                    const r = await a.run(() => del(org.api(`/condo/meetings/${id}`)), { success: "Assemblée supprimée.", refresh });
                    if (r) onClose();
                  }}
                >
                  Supprimer l’assemblée
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
        {resolution && m ? <ResolutionModal meetingId={id} next={(m.resolutions ?? []).reduce((x, r) => Math.max(x, r.number), 0) + 1} resolution={resolution === "new" ? null : resolution} onClose={() => setResolution(null)} /> : null}
      </div>
    </Modal>
  );
}

function ResolutionModal({ meetingId, next, resolution, onClose }: { meetingId: string; next: number; resolution: Resolution | null; onClose: () => void }) {
  const org = useOrg();
  const r = resolution;
  const [f, setF] = useState({
    number: String(r?.number ?? next),
    title: r?.title ?? "",
    description: r?.description ?? "",
    majority: r?.majority ?? "art24",
    estimated_cost_cents: r?.estimated_cost_cents ?? (null as number | null),
    intention: r?.intention ?? "a_decider",
    outcome: r?.outcome ?? "inconnue",
    source_ref: r?.source_ref ?? "",
    affects_charges: r?.affects_charges ?? false,
  });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title={r ? `Résolution n° ${r.number}` : "Nouvelle résolution"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const body = { ...f, number: Number(f.number || 0) };
              const res = await a.run(() => (r ? put(org.api(`/condo/meetings/${meetingId}/resolutions/${r.id}`), body) : post(org.api(`/condo/meetings/${meetingId}/resolutions`), body)), { success: "Résolution enregistrée.", refresh: [org.api("/condo/meetings")] });
              if (res) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <div className="form-row">
          <Input label="Numéro" inputMode="numeric" value={f.number} error={fieldError(a.error, "number")} onChange={(e) => setF({ ...f, number: e.target.value.replace(/\D/g, "") })} />
          <Select label="Majorité" value={f.majority} onChange={(e) => setF({ ...f, majority: e.target.value })} options={Object.entries(MAJORITY).map(([value, label]) => ({ value, label }))} />
        </div>
        <Input label="Intitulé" required value={f.title} error={fieldError(a.error, "title")} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Textarea label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className="form-row">
          <MoneyInput label="Coût estimé" cents={f.estimated_cost_cents} onChange={(c) => setF({ ...f, estimated_cost_cents: c })} />
          <Select label="Votre intention de vote" value={f.intention} onChange={(e) => setF({ ...f, intention: e.target.value })} options={Object.entries(INTENTION).map(([value, label]) => ({ value, label }))} />
        </div>
        <Checkbox label="Incidence sur les charges ou appels de fonds" checked={f.affects_charges} onChange={(v) => setF({ ...f, affects_charges: v })} />
        <div className="form-row">
          <Select label="Résultat (d’après le procès-verbal)" value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} options={Object.entries(OUTCOME).map(([value, label]) => ({ value, label }))} />
          <Input
            label="Passage source"
            placeholder="Ex. PV page 3, résolution 12"
            value={f.source_ref}
            help="Obligatoire pour enregistrer un résultat."
            error={fieldError(a.error, "source_ref")}
            onChange={(e) => setF({ ...f, source_ref: e.target.value })}
          />
        </div>
      </div>
    </Modal>
  );
}
